<template lang="pug">
  div
    .leaderboard
      .enforce-max-width
        b-row
          b-col.no-gutters(cols="12")
            // Fixed header area
            .leaderboard-header
              h1.text-center.mb-4.official-heading.text-shadow Beaver Jam Official Leaderboard

              // World Selection Button Bar
              .world-selector
                .connected-button-bar
                  button.world-btn.first(
                    :class="{ active: activeTab === 0 }",
                    @click="setActiveTab(0)"
                  ) All Worlds
                  button.world-btn(
                    v-for="(world, index) in worldOptions.slice(1)",
                    :key="world.value",
                    :class="{ active: activeTab === index + 1, last: index === worldOptions.length - 2 }",
                    @click="setActiveTab(index + 1)"
                  ) {{ world.text }}

            // Scrollable content area
            .leaderboard-content
              .content-viewport
                // Loading State
                .loading-state.text-center(v-if="loading")
                  b-spinner.align-middle.mx-2
                  span.text-shadow Loading leaderboard...

                // Error State
                b-alert(v-else-if="showError", variant="danger", dismissible)
                  strong Error loading leaderboard data.
                  p {{ errorMessage }}

                // Leaderboard Scores - App Style
                .leaderboard-scores(v-else-if="scores.length > 0")
                  .score-row(
                    v-for="(score, index) in scores",
                    :key="`${score.userId}-${score.timestamp}`"
                  )
                    .rank-section
                      .rank-badge(:class="[getRankClass(score.rank), getRankSizeClass(score.rank)]")
                        span.rank-number {{ score.rank }}
                    
                    .player-section
                      .player-name {{ score.displayName }}
                      .player-meta
                        span.world-info.d-inline(:class="`world-${score.world}`") World {{ score.world }}
                        span.date-info.d-none.d-md-inline {{ formatDate(score.timestamp) }}
                        span.date-info.d-inline.d-md-none {{ formatDateMobile(score.timestamp) }}
                    
                    .score-section
                      .score-value {{ formatScore(score.score) }}

                  .results-info.text-center.mt-3(v-if="allScores.length > 100 && activeTab === 0")
                    small.text-muted Showing top 100 of {{ allScores.length }} total scores

                // No Results State
                .empty-state(v-else-if="!loading")
                  h5.text-shadow.no-scores-title No scores found
                  p.text-shadow.no-scores-text Be the first to set a high score!

                // Information Card
                .info-section(v-if="showInstructions")
                  .info-content
                    button.close-btn(@click="dismissInstructions") ×
                    h4.info-title How to Join
                    ol.info-steps
                      li Complete all levels in #[span.beaverjam Beaver Jam] to unlock endless mode
                      li Tap "Leaderboard" from the main menu and enter your display name
                      li Play endless mode in any world to set high scores
                      li Your high scores will automatically appear on this global leaderboard!
                    p.info-note
                      strong Note:
                      |  Only players who have opted into the global leaderboard are shown. 
                      | Scores are updated in real-time when players submit new high scores.
</template>

<script>
export default {
  name: "Leaderboard",
  data() {
    return {
      activeTab: 0, // Default to All Worlds
      allScores: [], // Store all scores from API
      scores: [], // Filtered scores to display
      loading: true,
      showError: false,
      errorMessage: "",
      totalCount: 0,
      showInstructions: true, // New data property for instructions visibility

      worldOptions: [
        { value: 0, text: "All Worlds" },
        { value: 1, text: "World 1" },
        { value: 2, text: "World 2" },
        { value: 3, text: "World 3" },
        { value: 4, text: "World 4" },
        { value: 5, text: "World 5" }
      ],

      worldNames: {
        0: "all",
        1: "1",
        2: "2",
        3: "3",
        4: "4",
        5: "5"
      }
    };
  },
  computed: {
    currentWorld() {
      return this.worldNames[this.activeTab];
    },
    filteredScores() {
      if (this.activeTab === 0) {
        // Show all scores for "All Worlds"
        return this.allScores;
      } else {
        // Filter scores by selected world
        return this.allScores.filter(score => score.world === this.activeTab);
      }
    }
  },
  methods: {
    async fetchLeaderboard() {
      this.loading = true;
      this.showError = false;

      try {
        // Always fetch all scores (no world filter)
        const params = new URLSearchParams({
          limit: "500" // Increase limit to get more scores
        });

        const response = await this.$axios.get(
          `/.netlify/functions/leaderboard?${params.toString()}`
        );

        console.log("Leaderboard API response:", response.data);
        console.log("Scores array:", response.data.scores);
        console.log("Total count:", response.data.totalCount);

        this.allScores = response.data.scores || [];
        this.totalCount = response.data.totalCount || 0;

        // Update displayed scores based on current filter
        this.updateDisplayedScores();

        console.log("Vue component allScores:", this.allScores);
        console.log("Vue component scores:", this.scores);
        console.log("Vue component totalCount:", this.totalCount);
      } catch (error) {
        console.error("Error fetching leaderboard:", error);
        this.showError = true;
        this.errorMessage =
          error.response?.data?.error ||
          "Failed to load leaderboard data. Please try again later.";
        this.allScores = [];
        this.scores = [];
        this.totalCount = 0;
      } finally {
        this.loading = false;
      }
    },

    updateDisplayedScores() {
      // Get filtered scores and limit to top 100 for display
      const filtered = this.filteredScores;
      this.scores = filtered.slice(0, 100);

      // Re-rank the filtered scores based on their position in the filtered list
      this.scores.forEach((score, index) => {
        score.rank = index + 1;
      });
    },

    setActiveTab(tabIndex) {
      this.activeTab = tabIndex;
      // Just update the displayed scores, don't fetch again
      this.updateDisplayedScores();
    },

    getRankClass(rank) {
      if (rank <= 10) return "top-ten";
      return "standard";
    },

    getRankSizeClass(rank) {
      // Determine size class based on number of digits
      if (rank < 10) return "rank-single"; // 1 digit
      if (rank < 100) return "rank-double"; // 2 digits
      if (rank < 1000) return "rank-triple"; // 3 digits
      return "rank-quad"; // 4+ digits
    },

    formatScore(score) {
      return score.toLocaleString();
    },

    formatDate(timestamp) {
      if (!timestamp) return "Unknown";
      const date = new Date(timestamp * 1000);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric"
      });
    },

    formatDateMobile(timestamp) {
      if (!timestamp) return "Unknown";
      const date = new Date(timestamp * 1000);
      return date.toLocaleDateString("en-US", {
        month: "numeric",
        day: "numeric",
        year: "2-digit"
      });
    },

    dismissInstructions() {
      this.showInstructions = false;
    },

    loadMockData() {
      this.loading = true;

      // Generate 100 mock scores
      const mockScores = [];
      const playerNames = [
        "BeaverMaster",
        "DamBuilder",
        "WoodChomper",
        "RiverRunner",
        "TailSlapper",
        "LogJammer",
        "PondHopper",
        "StreamSwimmer",
        "BarkBiter",
        "LodgeKeeper",
        "AquaBeaver",
        "TimberTooth",
        "SplashKing",
        "DamDefender",
        "WaterWorker",
        "ChipMuncher",
        "FlowFighter",
        "CreekCrawler",
        "PaddlePaws",
        "MudSlinger"
      ];

      for (let i = 0; i < 100; i++) {
        const world = Math.floor(Math.random() * 5) + 1; // Random world 1-5
        const nameIndex = Math.floor(Math.random() * playerNames.length);
        const randomSuffix = Math.floor(Math.random() * 1000);

        mockScores.push({
          userId: `user_${i}`,
          displayName: `${playerNames[nameIndex]}${randomSuffix}`,
          score: Math.floor(Math.random() * 50000) + 1000, // Scores between 1000-51000
          world: world,
          timestamp: Date.now() / 1000 - i * 3600, // Stagger timestamps
          rank: i + 1 // Will be recalculated
        });
      }

      // Sort by score descending
      mockScores.sort((a, b) => b.score - a.score);

      this.allScores = mockScores;
      this.totalCount = mockScores.length;
      this.updateDisplayedScores();
      this.loading = false;
    }
  },

  created() {
    this.fetchLeaderboard();
    // Temporary: Load mock data for testing
    // this.loadMockData();
  }
};
</script>

<style scoped lang="less">
#app #router-content > div > div.leaderboard {
  padding: 0 var(--gutters) !important;

  @media (max-width: 1023px) and (min-width: 768px) {
    padding: 0 0.5rem !important;
  }

  @media (max-width: 767px) {
    padding: 0 0.5rem !important;
  }

  .no-gutters {
    padding: 0 !important;
  }

  .enforce-max-width {
    display: flex;
    flex-direction: column;
    max-width: 100%;
    padding: 0 !important;

    .row {
      display: flex;
      flex-direction: column;

      .col,
      .col-12,
      .col-md-10,
      .col-lg-8 {
        display: flex;
        flex-direction: column;
        overflow: visible; // Allow content to flow down naturally
      }
    }
  }

  .leaderboard-header {
    position: sticky;
    top: calc(var(--nav-height) + 20px);
    flex-shrink: 0; // Don't let header shrink
    z-index: 1000;
    padding: 20px 0;
    max-width: 66.66666666666666%;
    margin: 0 16.666666666666666%;

    @media (max-width: 1023px) and (min-width: 768px) {
      max-width: 70%;
      margin: 0 15%;
    }

    @media (max-width: 767px) {
      top: calc(var(--nav-height) + 10px);
      padding: 15px;
      max-width: 100%;
      margin: 0 !important;
    }
  }

  .leaderboard-content {
    flex: 1;
    position: relative;
    min-height: calc(
      100vh - var(--nav-height) - 165px
    ); // Ensure minimum viewport height
    top: 48px;

    @media (max-width: 767px) {
      top: 43px;
      min-height: calc(
        100vh - var(--nav-height) - 135px
      ); // Ensure minimum viewport height
    }

    .content-viewport {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      overflow-y: auto;
      padding-bottom: 80px; // Space for footer
      padding-top: 0.5rem;

      @media (max-width: 767px) {
        padding-bottom: 60px;
      }

      // Custom scrollbar for the content area
      scrollbar-width: thin;
      scrollbar-color: var(--secondary-color) rgba(0, 0, 0, 0.1);

      &::-webkit-scrollbar {
        width: 3px;
      }

      &::-webkit-scrollbar-track {
        background: rgba(0, 0, 0, 0.1);
        border-radius: 4px;
      }

      &::-webkit-scrollbar-thumb {
        background-color: var(--secondary-color);
        border-radius: 4px;

        &:hover {
          background-color: var(--secondary-color-hover);
        }
      }
    }
  }

  input,
  select {
    height: 40px;
  }

  h1 {
    font-family: var(--headings);
    color: var(--primary-color);
    font-weight: 500;

    &.official-heading {
      font-size: 1.8rem;

      @media (max-width: 767px) {
        font-size: 1.3rem;
      }
    }
  }

  .text-muted {
    color: rgba(
      var(--primaryR),
      var(--primaryG),
      var(--primaryB),
      0.5
    ) !important;
  }

  .text-shadow {
    text-shadow: -0.2px 0.4px 1.6px rgba(0, 0, 0, 0.1);
  }

  .sans-serif-font {
    font-family: var(--sans-serif);
  }

  // World Selection Button Bar
  .world-selector {
    margin-bottom: 0.5rem;

    .connected-button-bar {
      display: flex;
      background: transparent;
      padding: 0;
    }

    .world-btn {
      background: rgb(80, 88, 100);
      color: #e0e0e0;
      border: none;
      margin: 0;
      padding: 0.4rem 0.6rem;
      font-family: var(--headings);
      font-weight: normal;
      font-size: 12px;
      cursor: pointer;
      transition: none;
      flex: 1;
      text-transform: uppercase;
      letter-spacing: 0.8px;
      position: relative;
      min-height: 44px;

      // Retro beveled border effect (raised/inactive state) - wider and more subtle
      border-top: 3px solid rgba(255, 255, 255, 0.12);
      border-left: 3px solid rgba(255, 255, 255, 0.12);
      border-right: 3px solid rgba(0, 0, 0, 0.15);
      border-bottom: 3px solid rgba(0, 0, 0, 0.15);

      // Inner highlight - more subtle
      box-shadow: inset 1px 1px 0 rgba(255, 255, 255, 0.06),
        inset -1px -1px 0 rgba(0, 0, 0, 0.1);

      &:hover {
        background: rgb(65, 72, 85);
        color: #f0f0f0;
        // Keep same beveled borders with subtle contrast
        border-top: 3px solid rgba(255, 255, 255, 0.15);
        border-left: 3px solid rgba(255, 255, 255, 0.15);
        border-right: 3px solid rgba(0, 0, 0, 0.2);
        border-bottom: 3px solid rgba(0, 0, 0, 0.2);

        box-shadow: inset 1px 1px 0 rgba(255, 255, 255, 0.08),
          inset -1px -1px 0 rgba(0, 0, 0, 0.12);
      }

      &.active {
        background: rgb(100, 140, 170);
        color: #ffffff;
        // Remove beveled borders for flat selected state
        border-top: 3px solid transparent;
        border-left: 3px solid transparent;
        border-right: 3px solid transparent;
        border-bottom: 3px solid transparent;

        // Remove beveled shadow effect
        box-shadow: none;
      }
    }
  }

  // App-Style Score Rows
  .leaderboard-scores {
    max-width: 66.66666666666666%;
    margin: 0 16.666666666666666%;

    @media (max-width: 1023px) and (min-width: 768px) {
      max-width: 70%;
      margin: 0 15%;
    }

    @media (max-width: 767px) {
      max-width: 100%;
      margin: 0 !important;
      padding: 0 15px;
    }

    .score-row {
      display: flex;
      flex-direction: row; // Force row layout
      align-items: center;
      background: rgba(0, 84, 127, 0.33);
      margin-bottom: 0.5rem; // More space between rows
      padding: 0.1rem 0.5rem; // Even smaller padding
      transition: all 0.3s ease;
      min-height: 44px; // Constrain height

      // Mobile specific styling
      @media (max-width: 767px) {
        flex-direction: row !important; // Force row on mobile
        padding: 0.1rem 0.3rem; // Tighter padding on mobile
        gap: 0.5rem; // Add small gap between elements
      }

      &:hover {
        background: rgba(0, 84, 127, 0.45);
        transform: translateY(-1px);
        box-shadow: 0 2px 8px rgba(0, 84, 127, 0.3);
      }
    }

    .rank-section {
      flex-shrink: 0;
      margin-right: 0.75rem;
      display: flex;
      align-items: center;

      // Mobile adjustments
      @media (max-width: 767px) {
        margin-right: 0.5rem;
      }

      .rank-badge {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 28px; // Even smaller
        height: 28px;
        border-radius: 50%;
        font-family: var(--headings);
        font-weight: 500;
        font-size: 13px;

        // Mobile adjustments
        @media (max-width: 767px) {
          width: 24px;
          height: 24px;
          font-size: 11px;
        }

        .rank-number {
          // No special styling needed
        }

        &.top-ten {
          background: linear-gradient(
            135deg,
            var(--secondary-color),
            rgba(var(--secondaryR), var(--secondaryG), var(--secondaryB), 0.8)
          );
          color: white;
          box-shadow: 0 2px 4px
            rgba(var(--secondaryR), var(--secondaryG), var(--secondaryB), 0.3);
        }

        &.standard {
          background: var(--tertiary-color);
          color: var(--primary-color);
        }

        // Dynamic font sizing based on number of digits
        &.rank-single {
          font-size: 13px;
          @media (max-width: 767px) {
            font-size: 11px;
          }
        }

        &.rank-double {
          font-size: 11px;
          padding: 0 2px;
          @media (max-width: 767px) {
            font-size: 10px;
            padding: 0 1px;
          }
        }

        &.rank-triple {
          font-size: 9px;
          padding: 0 1px;
          letter-spacing: -0.5px;
          @media (max-width: 767px) {
            font-size: 8px;
            padding: 0;
            letter-spacing: -0.3px;
          }
        }

        &.rank-quad {
          font-size: 7px;
          padding: 0;
          letter-spacing: -0.5px;
          font-weight: 600;
          @media (max-width: 767px) {
            font-size: 6px;
            letter-spacing: -0.3px;
          }
        }
      }
    }

    .player-section {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: row; // Force row layout
      align-items: center;
      gap: 0.75rem;

      // Mobile adjustments
      @media (max-width: 767px) {
        flex-direction: row !important; // Force row on mobile
        gap: 0.5rem;
      }

      .player-name {
        font-family: "Courier New", Courier, monospace;
        font-weight: normal;
        font-size: 15px;
        color: var(--primary-color);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        flex-shrink: 0; // Don't let name truncate
        font-weight: 600;

        // Mobile adjustments
        @media (max-width: 767px) {
          font-size: 13px;
        }
      }

      .player-meta {
        display: flex;
        gap: 0.5rem;
        font-size: 11px;
        color: rgba(var(--primaryR), var(--primaryG), var(--primaryB), 0.7);
        flex-shrink: 1; // Allow meta section to shrink
        align-items: center;
        min-width: 0; // Allow shrinking

        // Mobile adjustments
        @media (max-width: 767px) {
          gap: 0.25rem;
          font-size: 10px;
        }

        .world-info {
          padding: 0.125rem 0.375rem;
          margin-right: 0.25rem;
          border-radius: 8px;
          font-weight: 500;
          font-size: 9px;
          text-transform: uppercase;
          flex-shrink: 0; // Don't let world info shrink

          // More vibrant pastel colors for each world
          &.world-1 {
            background: rgba(50, 205, 50, 0.75); // Lime green
            color: #0d4f0d;
          }

          &.world-2 {
            background: rgba(255, 179, 71, 0.75); // Vibrant orange
            color: #8b4500;
          }

          &.world-3 {
            background: rgba(64, 224, 208, 0.75); // Turquoise
            color: #006b5f;
          }

          &.world-4 {
            background: rgba(30, 144, 255, 0.75); // Dodger blue
            color: #001a33;
          }

          &.world-5 {
            background: rgba(255, 105, 180, 0.75); // Hot pink
            color: #8b1538;
          }
        }

        .date-info {
          font-weight: 500;
          font-size: 10px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          flex-shrink: 1; // Allow date to truncate
          min-width: 0; // Allow shrinking

          // Mobile adjustments
          @media (max-width: 767px) {
            font-size: 9px;
          }
        }
      }
    }

    .score-section {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      margin-left: 0.5rem;

      // Mobile adjustments
      @media (max-width: 767px) {
        margin-left: 0.25rem;
      }

      .score-value {
        font-family: var(--headings);
        font-weight: normal; // Not bold
        font-size: 18px;
        color: white; // White as requested
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
        line-height: 1;
        letter-spacing: 0;

        // Mobile adjustments
        @media (max-width: 767px) {
          font-size: 16px;
        }
      }
    }
  }

  // Loading State
  .loading-state {
    padding: 3rem;
    color: rgba(var(--primaryR), var(--primaryG), var(--primaryB), 0.7);

    span {
      font-weight: normal;
    }
  }

  // Empty State (simplified, no container)
  .empty-state {
    text-align: center;
    padding: 2rem 0;

    .no-scores-title {
      color: rgba(var(--primaryR), var(--primaryG), var(--primaryB), 0.7);
      font-family: var(--headings);
      font-weight: 500;
      margin-bottom: 1rem;
    }

    .no-scores-text {
      color: rgba(var(--primaryR), var(--primaryG), var(--primaryB), 0.7);
      font-size: 14px;
      margin-bottom: 0;
    }
  }

  // Info Section
  .info-section {
    max-width: 66.66666666666666%;
    margin: 0.5rem 16.666666666666666% 0;

    @media (max-width: 1023px) and (min-width: 768px) {
      max-width: 70%;
      margin: 0 15%;
    }

    @media (max-width: 767px) {
      max-width: 100%;
      margin: 0 !important;
      padding: 0 15px;
    }

    .info-content {
      background: rgba(var(--darkBG-R), var(--darkBG-G), var(--darkBG-B), 0.8);
      border-radius: 6px;
      padding: 20px 40px;
      position: relative;

      .close-btn {
        position: absolute;
        top: 15px;
        right: 15px;
        background: none;
        border: none;
        color: var(--faded-color);
        font-size: 45px;
        font-weight: bold;
        cursor: pointer;
        padding: 0;
        width: 20px;
        height: 28px;
        display: flex;
        align-items: center;
        justify-content: center;
        line-height: 1;
        transition: all 0.2s ease;

        &:hover {
          color: var(--primary-color);
          transform: scale(1.1);
        }

        &:active {
          transform: scale(0.95);
        }
      }
    }

    .info-title {
      color: var(--secondary-color);
      font-family: var(--headings);
      font-weight: 600;
      margin-bottom: 1rem;
    }

    .info-steps {
      color: var(--primary-color);
      margin-bottom: 1rem;
      text-align: left;
      font-family: var(--sans-serif);
      font-size: 12px;
      letter-spacing: 1px;
      padding-left: 2rem;

      li {
        margin-bottom: 0.5rem;
        line-height: 1.6;
      }
    }

    .info-note {
      font-size: 10px;
      color: var(--faded-color);
      margin-bottom: 0;
      line-height: 1.6;
      max-width: 480px;
      margin: 0 auto;

      @media (max-width: 767px) {
        max-width: 80vw;
      }

      strong {
        color: var(--primary-color);
      }
    }

    p {
      color: var(--primary-color);
      font-family: var(--sans-serif);
      font-size: 14px;
      letter-spacing: 1px;
    }
  }

  .results-info {
    padding: 1rem;
    color: var(--faded-color);
  }

  // Add space after the last content element to prevent footer overlap
  .info-section {
    margin-bottom: 80px;
  }

  // Beaver Jam text treatment
  .beaverjam {
    font-family: var(--headings);
    letter-spacing: -1px;
    font-weight: 500;
    font-size: 16px;
    line-height: 14px;
  }
}

// Mobile Responsive
@media (max-width: 767px) {
  .leaderboard {
    .leaderboard-header {
      padding: 10px 0;
    }

    .leaderboard-content {
      padding-bottom: 20px;
    }

    h1 {
      font-size: 1.5rem;
      margin-bottom: 1rem !important;
    }

    .world-selector {
      .connected-button-bar {
        flex-wrap: wrap;
      }

      .world-btn {
        padding: 0.5rem 0.75rem;
        font-size: 11px;
        flex: 1 1 auto;
        min-width: 70px;
      }
    }

    .leaderboard-scores {
      .score-row {
        padding: 0.375rem 0.5rem;
        text-align: left;

        .rank-section {
          margin-right: 0;
          align-self: center;
        }

        .player-section {
          width: 100%;
          text-align: center;

          .player-name {
            font-size: 14px;
          }

          .player-meta {
            justify-content: center;
          }
        }

        .score-section {
          align-self: center;
          margin-left: 0;

          .score-value {
            font-size: 16px;
          }
        }
      }
    }

    .info-section .info-content {
      padding: 1rem 0.5rem !important;

      .close-btn {
        top: 10px;
        right: 15px;
        font-size: 24px;
        width: 28px;
        height: 28px;
      }
    }

    .info-section {
      margin-bottom: 60px; // Slightly less on mobile
    }
  }
}

@media (max-width: 480px) {
  .leaderboard {
    .info-section {
      .info-content .close-btn {
        font-size: 22px;
        width: 26px;
        height: 26px;
        top: 8px;
        right: 12px;
      }
    }

    .world-selector {
      .world-btn {
        padding: 0.4rem 0.5rem !important;
        font-size: 10px !important;
        min-width: 60px;
        letter-spacing: 0 !important;
      }
    }

    .leaderboard-scores {
      .score-row {
        .player-section .player-name {
          font-size: 13px;
        }

        .score-section .score-value {
          font-size: 14px;
        }
      }
    }
  }
}
</style>
